/**
 * State Check-In — Pre-session self-assessment.
 * Phase 1: Rate energy, certainty, clarity (1-10).
 * Phase 2 (conditional): If any score < 5, show breathing + gratitude priming.
 * Props: onComplete(scores), onSkip()
 */

import { useState, useEffect, useRef, useCallback } from 'react';

const DIMENSIONS = [
  { key: 'energy', label: 'Energy', description: 'How charged up do you feel right now?', low: 'Running on empty', high: 'Fully charged' },
  { key: 'certainty', label: 'Certainty', description: 'How clear are you on what you need to do next?', low: 'No idea', high: 'Crystal clear' },
  { key: 'clarity', label: 'Clarity', description: 'How well can you think through problems right now?', low: 'Foggy', high: 'Sharp' },
];

function pillColor(value) {
  if (value <= 3) return 'bg-red-600 text-white';
  if (value <= 5) return 'bg-amber-600 text-white';
  if (value <= 8) return 'bg-blue-600 text-white';
  return 'bg-green-600 text-white';
}

function pillInactive(value) {
  if (value <= 3) return 'border-red-900/50 text-red-400 hover:bg-red-900/30';
  if (value <= 5) return 'border-amber-800/40 text-amber-400 hover:bg-amber-900/30';
  if (value <= 8) return 'border-blue-800/40 text-blue-400 hover:bg-blue-900/30';
  return 'border-green-700/50 text-green-400 hover:bg-green-900/30';
}

function ScoreRow({ dimension, value, onChange }) {
  return (
    <div className="mb-6">
      <div className="mb-2">
        <span className="text-base font-medium text-slate-200">{dimension.label}</span>
        <p className="text-sm text-slate-400">{dimension.description}</p>
      </div>
      {/* 5x2 grid on phones so each button stays a readable tap target; single row from sm up */}
      <div className="grid grid-cols-5 sm:grid-cols-10 gap-1.5">
        {Array.from({ length: 10 }, (_, i) => i + 1).map(n => (
          <button
            key={n}
            onClick={() => onChange(n)}
            className={`min-h-tap py-2 rounded-md text-base font-medium transition-colors border ${
              value === n
                ? pillColor(n)
                : `bg-transparent ${pillInactive(n)}`
            }`}
          >
            {n}
          </button>
        ))}
      </div>
      {/* Scale anchors so 1 and 10 mean something concrete */}
      <div className="flex justify-between mt-1.5">
        <span className="text-sm text-slate-400">1 = {dimension.low}</span>
        <span className="text-sm text-slate-400">10 = {dimension.high}</span>
      </div>
    </div>
  );
}

// Breathing phases: inhale 4s, hold 4s, exhale 4s = 12s per cycle, 3 cycles = 36s
const BREATH_PHASES = [
  { label: 'Breathe in', duration: 4000, scale: 'scale-100' },
  { label: 'Hold', duration: 4000, scale: 'scale-100' },
  { label: 'Breathe out', duration: 4000, scale: 'scale-75' },
];
const TOTAL_CYCLES = 3;
const READY_EARLY_MS = 30000;
const READY_PRIMARY_MS = 60000;

function PrimingExercise({ onComplete, onSkip }) {
  const [phase, setPhase] = useState('breathing'); // 'breathing' | 'gratitude'
  const [breathPhaseIdx, setBreathPhaseIdx] = useState(0);
  const [cycle, setCycle] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [gratitudeText, setGratitudeText] = useState('');
  const startTime = useRef(Date.now());

  // Track elapsed time
  useEffect(() => {
    const timer = setInterval(() => {
      setElapsed(Date.now() - startTime.current);
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Breathing cycle automation
  useEffect(() => {
    if (phase !== 'breathing') return;

    const currentPhase = BREATH_PHASES[breathPhaseIdx];
    const timer = setTimeout(() => {
      const nextIdx = breathPhaseIdx + 1;
      if (nextIdx >= BREATH_PHASES.length) {
        const nextCycle = cycle + 1;
        if (nextCycle >= TOTAL_CYCLES) {
          setPhase('gratitude');
        } else {
          setCycle(nextCycle);
          setBreathPhaseIdx(0);
        }
      } else {
        setBreathPhaseIdx(nextIdx);
      }
    }, currentPhase.duration);

    return () => clearTimeout(timer);
  }, [phase, breathPhaseIdx, cycle]);

  const canFinishEarly = elapsed >= READY_EARLY_MS;
  const canFinishPrimary = elapsed >= READY_PRIMARY_MS;
  const currentBreath = BREATH_PHASES[breathPhaseIdx];

  if (phase === 'breathing') {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4">
        <p className="text-slate-400 text-base mb-8">Take a moment to settle in.</p>

        {/* Breathing circle */}
        <div className="relative flex items-center justify-center mb-8">
          <div
            className={`w-32 h-32 rounded-full border-2 border-blue-500 transition-transform ease-in-out flex items-center justify-center ${
              currentBreath.scale === 'scale-100' ? 'scale-100' : 'scale-75'
            }`}
            style={{ transitionDuration: `${currentBreath.duration}ms` }}
          >
            <span className="text-blue-400 text-sm font-medium">{currentBreath.label}</span>
          </div>
        </div>

        <p className="text-slate-400 text-sm mb-6">
          Cycle {cycle + 1} of {TOTAL_CYCLES}
        </p>

        <button
          onClick={onSkip}
          className="min-h-tap text-slate-400 text-sm hover:text-slate-300 transition-colors"
        >
          Skip
        </button>
      </div>
    );
  }

  // Gratitude phase
  return (
    <div className="flex flex-col items-center justify-center py-12 px-4 max-w-md mx-auto">
      <p className="text-slate-200 text-base mb-2 font-medium">One more thing.</p>
      <p className="text-slate-400 text-base mb-6 text-center">
        Name one thing you're grateful for right now — something specific, not generic.
      </p>

      <textarea
        value={gratitudeText}
        onChange={(e) => setGratitudeText(e.target.value)}
        placeholder="e.g. My daughter laughed at breakfast today..."
        rows={2}
        className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-base text-slate-200 placeholder-slate-500 resize-none focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 mb-6"
      />

      <div className="flex gap-3">
        <button
          onClick={onComplete}
          className={`min-h-tap px-6 py-2.5 rounded-lg text-base font-medium transition-colors ${
            canFinishPrimary
              ? 'bg-blue-600 hover:bg-blue-500 text-white'
              : canFinishEarly
                ? 'bg-slate-700 hover:bg-slate-600 text-slate-300'
                : 'bg-slate-800 text-slate-500 cursor-not-allowed'
          }`}
          disabled={!canFinishEarly}
        >
          I'm ready
        </button>
        <button
          onClick={onSkip}
          className="min-h-tap text-slate-400 text-sm hover:text-slate-300 transition-colors px-3 py-2"
        >
          Skip
        </button>
      </div>
    </div>
  );
}

export default function StateCheckIn({ onComplete, onSkip }) {
  const [scores, setScores] = useState({ energy: null, certainty: null, clarity: null });
  const [phase, setPhase] = useState('checkin'); // 'checkin' | 'priming'

  const allScored = scores.energy !== null && scores.certainty !== null && scores.clarity !== null;
  const needsPriming = allScored && Object.values(scores).some(v => v < 5);

  const handleContinue = useCallback(() => {
    if (needsPriming) {
      setPhase('priming');
    } else {
      onComplete(scores);
    }
  }, [needsPriming, scores, onComplete]);

  const handlePrimingComplete = useCallback(() => {
    onComplete(scores);
  }, [scores, onComplete]);

  if (phase === 'priming') {
    return <PrimingExercise onComplete={handlePrimingComplete} onSkip={() => onComplete(scores)} />;
  }

  return (
    <div className="flex flex-col items-center justify-center py-12 px-4 max-w-2xl mx-auto">
      <p className="text-xl font-medium text-slate-200 mb-2">Before we begin</p>
      <p className="text-slate-400 text-base mb-8">How are you showing up today?</p>

      <div className="w-full">
        {DIMENSIONS.map(dim => (
          <ScoreRow
            key={dim.key}
            dimension={dim}
            value={scores[dim.key]}
            onChange={(val) => setScores(prev => ({ ...prev, [dim.key]: val }))}
          />
        ))}
      </div>

      <div className="flex gap-3 mt-4">
        <button
          onClick={handleContinue}
          disabled={!allScored}
          className="min-h-tap px-6 py-2.5 rounded-lg text-base font-medium transition-colors bg-blue-600 hover:bg-blue-500 text-white disabled:bg-slate-700 disabled:text-slate-500"
        >
          Continue
        </button>
        <button
          onClick={onSkip}
          className="min-h-tap px-4 py-2.5 text-slate-400 text-sm hover:text-slate-300 transition-colors"
        >
          Skip
        </button>
      </div>
    </div>
  );
}
